import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Scrollbar, A11y, Mousewheel, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import styles from './Css/RomanticAudioBook.module.css';
import { Headphones } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUniqueAudiosByCategory } from '../../hooks/useChapters';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

const AudiobookItem = ({ audio }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use mobile image on mobile devices, computer image on desktop
  const image = isMobile
    ? audio.mobile_image_url
    : audio.computer_image_url;
  
  return (
    <div className={styles.audiobook_item} onClick={()=>navigate(`audio-play/${audio.audio_slug}`)}>
      <img src={image || audio.cover_image_url} alt={audio.audio_title} className={styles.audiobook_image} />
      <div className={styles.audiobook_content}>
        <div className={styles.audiobook_header}>
          <h3 className={styles.audiobook_title}>{audio.audio_title}</h3>
          <p className={styles.audiobook_time}>
            <Headphones size={16} />
            {audio.duration ? formatTime(audio.duration) : 'N/A'}
          </p>
        </div>
        <p className={styles.audiobook_description}>{audio.audio_description}</p>
      </div>
    </div>
  );
};

const AudiobookGrid = ({ title }) => {
  const navigate = useNavigate();
  const {
    audios,
    isLoading,
  } = useUniqueAudiosByCategory(title, 'Audio', 'Audio Book', 1, 12);

  if (isLoading) {
    return <div className={styles.loading}>Loading audiobooks...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerPart}>
        <h2 className={styles.heading}>{title} Audio Books</h2>
        <p className={styles.view_all} onClick={() => navigate(`/view-all/${title}`)}>View All</p>
      </div>
      
      <Swiper
        freeMode={{
            enabled: true,
            momentum: true,
            momentumRatio: 0.8,
            momentumVelocityRatio: 0.8,
            minimumVelocity: 0.02,
          }}
          modules={[Navigation, Mousewheel, FreeMode, Pagination, Scrollbar, A11y]}
          mousewheel={{
            forceToAxis: true,
            sensitivity: 1.5,
            releaseOnEdges: true,
          }}
          speed={600}
          resistance={false}
          resistanceRatio={0}
        spaceBetween={20}
        slidesPerView={1}
        navigation
        pagination={{ clickable: true }}
        breakpoints={{
          320: {
            slidesPerView: 3,
            spaceBetween: 10,
          },
          480: {
            slidesPerView: 3,
            spaceBetween: 15,
          },
          640: {
            slidesPerView: 3, // Show 3 items on mobile as requested
            spaceBetween: 15,
          },
          768: {
            slidesPerView: 4,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 7,
            spaceBetween: 20,
          },
        }}
        className={styles.audiobook_swiper}
      >
        {audios && audios.map((audioItem) => (
          <SwiperSlide key={audioItem.audio_id} className={styles.swiper_slide}>
            <AudiobookItem audio={audioItem} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default AudiobookGrid;